import React, { useState, useEffect } from 'react';
import Checkgif from "./Images/Check.gif";
import masterbg from "./Images/MasterBKG.png";
import Loading from '../../Common/Loading';

interface GameRow {
    FileName: string;
    ExistsInBlob: boolean;
    ExistsInSite: boolean;
    IsPlayable: boolean;
    PlayArg: string;
}

const EL_ManageGames: React.FC = () => {
    // --- State ---
    const [data, setData] = useState<GameRow[]>([]);
    const [title, setTitle] = useState('');
    const [isHTML5, setIsHTML5] = useState(true);
    const [loading, setLoading] = useState(false);
    const [activeRow, setActiveRow] = useState<number | null>(null); // ✅ added

    // --- Styles ---
    const styles = {
        body: {
            margin: '0px',
            backgroundColor: '#214461',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: 'Calibri'
        },
        masterBkg: {
            backgroundImage: `url(${masterbg})`,
            backgroundRepeat: 'no-repeat',
            width: '800px',
            height: '580px',
            overflow: 'hidden',
            paddingLeft: '50px',
            fontSize: '14pt',
            position: 'relative' as const
        },
        gridContainer: {
            height: '360px',
            width: '700px',
            overflow: 'auto',
            border: '1px solid #80a0ff',
            marginTop: '110px',
            backgroundColor: 'white'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontSize: '10pt',
            fontFamily: 'Arial',
            borderBottom: "1px solid #80a0ff"
        },
        th: {
            backgroundColor: '#c7ddfd',
            color: '#2859ac',
            fontWeight: 'normal',
            padding: '8px',
            border: '1px solid #80a0ff',
            position: 'sticky' as const,
            top: 0,
            textAlign: 'left' as const
        },
        td: {
            padding: '7px',
            borderRight: '1px solid #80a0ff',
            color: 'inherit'
        },
        tdCenter: {
            padding: '4px',
            borderRight: '1px solid #80a0ff',
            textAlign: 'center' as const,
            color: 'inherit'
        },
        headerDiv: {
            fontSize: '10pt',
            fontWeight: 'bold',
            padding: "6px",
            color: '#2859ac'
        },
        controlsDiv: {
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center'
        },
        checkbox: {
            marginLeft: '20px',
            fontSize: '12pt',
            cursor: 'pointer',
            color: 'black'
        },
        btn: {
            height: '30px',
            padding: '0 10px',
            cursor: 'pointer'
        }
    };

    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

    // --- Load Data ---
    useEffect(() => {
        fetchGames();
    }, [isHTML5]);

    const fetchGames = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/manage-games-list/?isHTML5=${isHTML5}`);
            const json = await res.json();
            setData(json.data);
            setTitle(json.title);
        } catch {
            alert("Failed to load games");
        } finally {
            setLoading(false);
        }
    };

    // --- Handlers ---
    const handlePlay = async (lessonCode: string) => {
        try {
            const res = await fetch(`${API_BASE}api/manage-games-play-url/?LessonCode=${lessonCode}&isHTML5=${isHTML5}`);
            const json = await res.json();

            if (json.url) {
                const width = window.innerWidth + 15;
                const height = window.innerHeight + 30;
                window.open(json.url, "GAME", `width=${width},height=${height},resizable=yes,scrollbars=yes`);
            }
        } catch {
            alert("Error launching game");
        }
    };

    const handleBack = () => {
        window.location.href = '/SysAdmin';
    };

    return (
        <div style={styles.body}>
            <div style={styles.masterBkg}>
                <div style={styles.gridContainer}>
                    <div
                        style={{ ...styles.headerDiv, backgroundColor: "#c7ddfd", textAlign: "center" }}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />

                    {loading ? <Loading/> : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'center' }}>#</th>
                                    <th style={styles.th}>File Name</th>
                                    <th style={{ ...styles.th, textAlign: 'center' }}>Exists In BLOB</th>
                                    <th style={{ ...styles.th, textAlign: 'center' }}>Exists in Site</th>
                                    <th style={styles.th}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, idx) => {
                                    const isActive = activeRow === idx;

                                    return (
                                        <tr
                                            key={idx}
                                            onClick={() => setActiveRow(idx)}
                                            style={{
                                                backgroundColor: isActive
                                                    ? 'orange'
                                                    : idx % 2 === 0
                                                        ? '#f4faff'
                                                        : 'white',
                                                color: isActive ? 'white' : 'black',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <td style={styles.tdCenter}>{idx + 1}</td>
                                            <td style={styles.td}>{row.FileName}</td>
                                            <td style={styles.tdCenter}>
                                                {row.ExistsInBlob && <img src={Checkgif} alt="Yes" />}
                                            </td>
                                            <td style={styles.tdCenter}>
                                                {row.ExistsInSite && <img src={Checkgif} alt="Yes" />}
                                            </td>
                                            <td style={styles.td}>
                                                {row.IsPlayable && (
                                                    <input
                                                        type="button"
                                                        value=" Play "
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePlay(row.PlayArg);
                                                        }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={styles.controlsDiv}>
                    <button onClick={handleBack} style={styles.btn}>Back to Menu</button>
                    <label style={styles.checkbox}>
                        <input
                            type="checkbox"
                            checked={isHTML5}
                            onChange={(e) => setIsHTML5(e.target.checked)}
                        /> HTML5 Games
                    </label>
                </div>
            </div>
        </div>
    );
};

export default EL_ManageGames;
